/**
 * A policy change, and which way it points — the whole reason `/policies` exists.
 *
 * `plans/sdd-execution-plan.md` Task 45: *"mỗi thay đổi hiện version mới + diff tóm
 * tắt"*. The version half is the endpoint's own doing —
 * `POST /admin/activity-catalog/websites` is keyed on
 * `(company, scope_type, scope_key, match_field, match_value, effective_from)` and
 * *"saving under an existing key updates the rule and increments its `version`"* — so
 * this file is the summary half, and the summary is only worth rendering if it
 * answers the question the administrator is actually asking:
 *
 *   **Does more of my staff's activity get recorded after this, or less?**
 *
 * A diff that lists `is_private: false → true` beside `category: neutral →
 * communication` in the same neutral grey has answered "what fields changed", which
 * is a question nobody had.
 *
 * ── Three directions, and the third is not a hedge ──────────────────────────
 *
 * `tightens` — more is recorded, or more people are judged by a stricter rule.
 * `loosens`  — less is recorded.
 * `judgement`— what is *collected* does not move; how it is *judged* does.
 *
 * The third exists because folding it into the first two would make the warning
 * useless. Every ordinary catalogue edit — reclassifying a vendor's site from
 * `uncategorized` to `research` — changes judgement and collects nothing new. If
 * that rendered as "tightens", the screen would cry wolf on the majority of its own
 * traffic, and a warning that fires on correct work is one whoever it blocks learns
 * to click past. This build has already recorded that failure mode twice, about
 * lint rules; it applies to copy as well.
 *
 * ── Why a new non-private rule is `judgement` and not `tightens` ────────────
 *
 * Because the host was *already* being recorded. Task 33 keeps URLs domain-only
 * under collection level 1, which is the default, so a domain with no catalogue rule
 * at all is still landing in `wp_activity_events` by name. Adding a rule that
 * classifies it collects nothing that was not already collected. What *does* move
 * the line is `is_private`, in either direction, and that is the change this module
 * is built to make impossible to miss.
 */

import type { ActivityCategory, ProductivityClass, WebsiteCatalogEntry } from '../api/types';

/** The draft a `POST /admin/activity-catalog/websites` would send. */
export interface WebsiteRuleDraft {
  readonly match_value: string;
  readonly match_field: 'domain' | 'domain_suffix';
  readonly category: ActivityCategory;
  readonly productivity_class?: ProductivityClass;
  readonly is_private: boolean;
  readonly is_blacklisted: boolean;
  readonly scope_type: 'company' | 'department' | 'job_title';
  readonly scope_key: string | null;
}

export const CHANGE_DIRECTIONS = ['tightens', 'loosens', 'judgement'] as const;

export type ChangeDirection = (typeof CHANGE_DIRECTIONS)[number];

/**
 * One line of the summary.
 *
 * `field` is the wire name so the refusal handler can line a `400`'s
 * `details.issues` entry up with the change that caused it; `messageKey` is the
 * sentence, chosen from a closed set rather than assembled, because a sentence built
 * by string concatenation cannot be translated and this catalogue is Vietnamese
 * first.
 */
export interface PolicyChange {
  readonly field: 'is_private' | 'is_blacklisted' | 'category' | 'productivity_class' | 'rule';
  readonly direction: ChangeDirection;
  readonly messageKey: PolicyChangeKey;
  readonly host: string;
  /** Rendered into the sentence where one is needed. Never a raw enum on screen. */
  readonly from?: string;
  readonly to?: string;
}

export type PolicyChangeKey =
  | 'policies.change.private.on'
  | 'policies.change.private.off'
  | 'policies.change.blacklist.on'
  | 'policies.change.blacklist.off'
  | 'policies.change.category'
  | 'policies.change.class'
  | 'policies.change.new.private'
  | 'policies.change.new.named';

/**
 * The existing rule this draft would replace, or `null` when it creates a new one.
 *
 * Matched on the endpoint's own key minus `effective_from`, which this screen never
 * sends — so a save here always lands on the current `effective_from` default and
 * therefore always *updates* an existing rule rather than silently creating a second
 * one dated today. The screen states that; a screen that let the operator pick a
 * future date without saying so would produce two live rules and one confused
 * administrator.
 */
export function matchExisting(
  rules: readonly WebsiteCatalogEntry[],
  draft: WebsiteRuleDraft,
): WebsiteCatalogEntry | null {
  const host = draft.match_value.trim().toLowerCase();
  return (
    rules.find(
      (rule) =>
        rule.match_value.trim().toLowerCase() === host &&
        rule.match_field === draft.match_field &&
        rule.scope_type === draft.scope_type &&
        (rule.scope_type === 'company' || (rule.scope_key ?? null) === draft.scope_key),
    ) ?? null
  );
}

/**
 * The summary. Empty when the draft changes nothing — which is a real case and gets
 * its own sentence on screen, because a save button that is live over an empty diff
 * mints a version number for nothing.
 */
export function summarise(
  draft: WebsiteRuleDraft,
  existing: WebsiteCatalogEntry | null,
): readonly PolicyChange[] {
  const host = draft.match_value.trim();
  if (existing === null) {
    return [
      draft.is_private
        ? {
            field: 'rule',
            direction: 'loosens',
            messageKey: 'policies.change.new.private',
            host,
          }
        : {
            field: 'rule',
            direction: 'judgement',
            messageKey: 'policies.change.new.named',
            host,
          },
    ];
  }

  const changes: PolicyChange[] = [];
  const wasPrivate = existing.is_private === true;
  const wasBlacklisted = existing.is_blacklisted === true;

  if (wasPrivate !== draft.is_private) {
    changes.push(
      draft.is_private
        ? {
            field: 'is_private',
            direction: 'loosens',
            messageKey: 'policies.change.private.on',
            host,
          }
        : {
            field: 'is_private',
            direction: 'tightens',
            messageKey: 'policies.change.private.off',
            host,
          },
    );
  }

  if (wasBlacklisted !== draft.is_blacklisted) {
    changes.push(
      draft.is_blacklisted
        ? {
            field: 'is_blacklisted',
            direction: 'tightens',
            messageKey: 'policies.change.blacklist.on',
            host,
          }
        : {
            field: 'is_blacklisted',
            direction: 'loosens',
            messageKey: 'policies.change.blacklist.off',
            host,
          },
    );
  }

  if (existing.category !== draft.category) {
    changes.push({
      field: 'category',
      direction: 'judgement',
      messageKey: 'policies.change.category',
      host,
      from: existing.category,
      to: draft.category,
    });
  }

  const wasClass = existing.productivity_class ?? null;
  const nowClass = draft.productivity_class ?? null;
  if (wasClass !== nowClass) {
    changes.push({
      field: 'productivity_class',
      direction: 'judgement',
      messageKey: 'policies.change.class',
      host,
      from: wasClass ?? 'none',
      to: nowClass ?? 'none',
    });
  }

  return changes;
}

/**
 * Does this diff need the operator to acknowledge it before the save is live?
 *
 * **Only when something tightens.** Not "when anything changed": see this file's
 * header on warnings that fire on correct work. A change that loosens surveillance
 * needs no ceremony — the person it protects is not in the room to object, and
 * making it harder to *stop* watching somebody would be this screen taking a side.
 */
export function requiresAcknowledgement(changes: readonly PolicyChange[]): boolean {
  return changes.some((change) => change.direction === 'tightens');
}

/**
 * `details.issues` from a `400 WORKFORCE_VALIDATION_FAILED`, as the field names the
 * server used.
 *
 * Read off the refusal rather than guessed, because the server is the authority on
 * which field it refused and this screen has no second copy of its rules. The three
 * refusals reachable from here are a `regex` `match_value` that does not compile, an
 * `exact` `match_value` carrying a scheme/path/query/fragment, and a `scope_key`
 * that disagrees with `scope_type`; all three name `match_value` or `scope_key`.
 *
 * Accepts both shapes the error body has been seen to carry — an array of strings,
 * and an array of `{ path }` objects — because `details` is `Record<string,
 * unknown>` on the wire and a client that assumed one shape would render nothing at
 * all for the other, which is exactly the silent branch this screen must not have.
 */
export function refusedFields(details: Readonly<Record<string, unknown>>): readonly string[] {
  const issues = details.issues;
  if (!Array.isArray(issues)) return [];
  const fields: string[] = [];
  for (const issue of issues) {
    if (typeof issue === 'string') {
      fields.push(issue);
      continue;
    }
    if (issue !== null && typeof issue === 'object') {
      const record = issue as Record<string, unknown>;
      const path = record.path ?? record.field;
      if (typeof path === 'string') fields.push(path);
      else if (Array.isArray(path) && typeof path[0] === 'string') fields.push(path[0]);
    }
  }
  return fields;
}
